/**
 * R4 Soll-Versteuerung: je Sollstellung (WARM_MIETE, KALT_MIETE, BK, CUSTOM, SP) genau eine
 * Zeile im Leistungsmonat (R1), unabhängig von der Zahlung. Betriebskosten im Abrechnungsmonat,
 * Guthaben/PAYOUT negativ. gross = amount − Minderungen, die bis zum Leistungsmonat erfasst sind;
 * spätere Minderungen laufen über R7 (minderung.ts).
 *
 * Leistungen in Kleinunternehmer-/steuerfreien Jahren werden als KU_ALTUMSATZ (Kz NONE) geführt,
 * steuerfreie Kategorien landen unabhängig vom Profil in Kz 48 (Nachweis § 4 Nr. 12).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from '../issues';
import {buildLine, setupDeepLink} from '../line';
import {compareMonthKey, monthKey, monthLabel, monthStart, resolveLeistungsmonat} from '../month';
import {isTaxableCategory} from '../resolve-vat-category';
import {round2, signedAmount} from '../rounding';
import {BookingArtLite, BookingLite, EMPTY_RULE_OUTPUT, RentReductionLite, RuleContext, RuleOutput, VatIssue, VatLine} from '../types';
import {buildExcludedLine, classifyBooking, issueIDsOf, linkIssues} from './exclusions';
import {classifyBookingForSoll} from './profile-wechsel';

export const ART_LABELS: Record<BookingArtLite, string> = {
    WARM_MIETE: 'Warmmiete',
    KALT_MIETE: 'Kaltmiete',
    KAUTION: 'Kaution',
    BETRIEBSKOSTEN: 'Betriebskosten',
    DUNNING_FEES: 'Mahngebühren',
    INTEREST_EXPENSES: 'Verzugszinsen',
    SP: 'Sonderposten',
    CUSTOM: 'Sollstellung',
    GUTHABEN: 'Guthaben',
};

export const artLabel = (art: BookingArtLite | null): string => (art ? ART_LABELS[art] ?? 'Sollstellung' : 'Sollstellung');

export const reductionMonthKey = (reduction: RentReductionLite): string | null => {
    const date = reduction.date ?? reduction.createdAt;
    return date ? monthKey(date) : null;
};

/** Minderungen mit Erfassungsmonat ≤ Leistungsmonat senken das Soll direkt (R4); ohne Liste zählt die Gesamtsumme. */
export const sameMonthReductionTotal = (booking: BookingLite, leistungsMonthKey: string): number => {
    if (booking.rentReductionList.length === 0) {
        return round2(booking.rentReductionAmount);
    }
    return round2(booking.rentReductionList
        .filter(r => {
            const key = reductionMonthKey(r);
            return key === null || compareMonthKey(key, leistungsMonthKey) <= 0;
        })
        .reduce((sum, r) => sum + Math.abs(r.amount), 0));
};

export const sollGross = (booking: BookingLite, leistungsMonthKey: string): number => {
    const base = signedAmount(booking.amount, booking.type);
    const reductions = booking.type === 'PAYOUT' ? 0 : sameMonthReductionTotal(booking, leistungsMonthKey);
    return round2(base - reductions);
};

/**
 * Offener Betrag einer Sollstellung, app-weit „Rest nach Minderung“: amount − rentReductionAmount − haben.
 * `haben` enthält die Minderung nicht (booking-after-rent-reduction.pipe, create-booking-payment-dialog),
 * daher werden ALLE Minderungen abgezogen – unabhängig vom Erfassungsmonat.
 */
export const openGross = (booking: BookingLite): number => {
    const reductions = booking.type === 'PAYOUT' ? 0 : round2(Math.abs(booking.rentReductionAmount));
    return round2(signedAmount(booking.amount, booking.type) - reductions - booking.haben);
};

const warmSplitIssue = (booking: BookingLite, deepLink: string): VatIssue | null => {
    if (!booking.divisionWarm || booking.art !== 'WARM_MIETE') {
        return null;
    }
    const sum = round2((booking.divisionWarm.kalt ?? 0) + (booking.divisionWarm.bk ?? 0) + (booking.divisionWarm.hk ?? 0));
    if (sum === 0 || Math.abs(sum - Math.abs(booking.amount ?? 0)) <= 0.01) {
        return null;
    }
    return createIssue('WARM_SPLIT_INCONSISTENT', booking.id,
        `Die Aufteilung der Warmmiete (Kalt ${booking.divisionWarm.kalt ?? 0} + BK ${booking.divisionWarm.bk ?? 0} + HK ${booking.divisionWarm.hk ?? 0} = ${sum}) passt nicht zum Betrag ${booking.amount}.`,
        {deepLink});
};

export const buildSollLines = (ctx: RuleContext, bookings: BookingLite[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();

    for (const booking of bookings) {
        const c = classifyBooking(ctx, booking);
        const lm = resolveLeistungsmonat(booking);
        const gross = signedAmount(booking.amount, booking.type);

        if (c.excluded) {
            const key = lm?.monthKey ?? monthKey(ctx.range.start);
            const line = buildExcludedLine(c, key, lm?.monthSource ?? 'DATE', gross, `${artLabel(booking.art)} ${monthLabel(key)}`);
            out.lines.push(line);
            out.issues.push(...linkIssues(c.issues, line.id));
            continue;
        }

        // lm ist hier gesetzt – sonst wäre die Quelle als DATE_MISSING ausgeschlossen
        const leistungDate = monthStart(lm.monthKey);
        const treatment = classifyBookingForSoll(ctx.profileAt, leistungDate);
        if (treatment === 'VIA_PAYMENTS') {
            continue;
        }

        const issues: VatIssue[] = [...c.issues];
        if (lm.highDayFallback) {
            issues.push(createIssue('LEISTUNGSMONAT_AUS_DATUM', booking.id,
                `Der Leistungsmonat ${monthLabel(lm.monthKey)} wurde aus dem Fälligkeitsdatum (Tag ${booking.date.getDate()}) abgeleitet. Bitte bestätigen, falls die Sollstellung den Folgemonat betrifft.`,
                {deepLink: c.source.deepLink}));
        }
        const split = warmSplitIssue(booking, c.source.deepLink);
        if (split) {
            issues.push(split);
        }

        const label = `${artLabel(booking.art)} ${monthLabel(lm.monthKey)}`;
        const grossAfterReductions = sollGross(booking, lm.monthKey);

        let line: VatLine;
        if (treatment === 'KU_ALTUMSATZ' && (c.category === null || isTaxableCategory(c.category))) {
            if (ctx.profileAt(leistungDate).status === 'STEUERFREI') {
                issues.push(createIssue('TAXABLE_AT_STEUERFREI_PROFILE', c.periodID ?? booking.id,
                    `Das Mietverhältnis ist steuerpflichtig erfasst, das Steuerprofil des Eigentümers ist für ${leistungDate.getFullYear()} aber „steuerfrei“. Bitte Profil oder Kategorie prüfen.`,
                    {deepLink: setupDeepLink(ctx.landlordID)}));
            }
            line = buildLine({
                rule: 'KU_ALTUMSATZ', source: c.source, monthKey: lm.monthKey, monthSource: lm.monthSource,
                category: c.category, rate: 0, gross: grossAfterReductions,
                label: `${label} – Leistung im Kleinunternehmer-/steuerfreien Jahr`, issueIDs: issueIDsOf(issues),
            });
        } else {
            const rate = treatment === 'KU_ALTUMSATZ' ? 0 : c.rate;
            line = buildLine({
                rule: 'SOLL', source: c.source, monthKey: lm.monthKey, monthSource: lm.monthSource,
                category: c.category, rate, gross: grossAfterReductions, label, issueIDs: issueIDsOf(issues),
            });
        }
        out.lines.push(line);
        out.issues.push(...linkIssues(issues, line.id));
    }

    return out;
};
